import { useQuery } from '@apollo/client/react';
import { GET_CHANNEL } from '@optools/services/operator-gateway/queries';
import { mergeChannelTargetsOnLoad } from '@optools/tools/partner/utils/mapping-state';
import { createTargetId } from '@optools/tools/partner/utils/targets';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { PatchedChannel } from './usePartnerData';

type DataCenterTabSource = {
  channels: readonly { name: string }[];
  name: string;
};

/** Mapping UX when the route is driven by the `channel` search param / GET_CHANNEL. */
export function useChannelMappingSelection({
  activeDataCenterTab,
  channelMap,
  channelParam,
  dataCenters,
  setActiveDataCenterTab,
}: {
  channelParam: string;
  channelMap: Map<number, PatchedChannel>;
  dataCenters: readonly DataCenterTabSource[];
  activeDataCenterTab: string | null;
  setActiveDataCenterTab: Dispatch<SetStateAction<string | null>>;
}) {
  const getChannelResult = useQuery(GET_CHANNEL, {
    context: { skipErrorNotification: true },
    fetchPolicy: 'network-only',
    skip: !channelParam,
    variables: { name: channelParam },
  });

  const { error: channelError, refetch: channelRefetch } = getChannelResult;
  useEffect(() => {
    if (channelError) {
      toast.error('Channel Error', {
        action: { label: 'Retry', onClick: () => channelRefetch() },
        description: channelError.message,
        duration: Infinity,
      });
    }
  }, [channelError, channelRefetch]);

  const currentChannel = getChannelResult.data?.getChannel;
  const channelQueryLoading = getChannelResult.loading;

  const [currentChannelTargetIds, setCurrentChannelTargetIds] = useState(() => new Set<string>());
  const [selectedTargetIds, setSelectedTargetIds] = useState(() => new Set<string>());
  /** True if targets were toggled while GET_CHANNEL was loading; consumed on first settled response. */
  const targetsSelectedDuringChannelLoadRef = useRef(false);

  useEffect(() => {
    setCurrentChannelTargetIds(new Set());
    setSelectedTargetIds(new Set());
    targetsSelectedDuringChannelLoadRef.current = false;
  }, [channelParam]);

  const handleSelectTarget = useCallback(
    (targetId: string) => {
      if (channelParam && channelQueryLoading) {
        targetsSelectedDuringChannelLoadRef.current = true;
      }
      setSelectedTargetIds((currentTargets) => {
        const newTargets = new Set(currentTargets);
        void (newTargets.has(targetId) ? newTargets.delete(targetId) : newTargets.add(targetId));
        return newTargets;
      });
    },
    [channelParam, channelQueryLoading],
  );

  useEffect(() => {
    if (currentChannel) channelMap.set(currentChannel.id, currentChannel);

    const targets = currentChannel?.targets;
    // Wait for a settled response before overwriting selection from the server.
    if (!channelQueryLoading && targets) {
      const serverTargetIds = new Set(targets.map(createTargetId));
      setCurrentChannelTargetIds(serverTargetIds);
      const editedDuringLoad = targetsSelectedDuringChannelLoadRef.current;
      if (editedDuringLoad) {
        targetsSelectedDuringChannelLoadRef.current = false;
      }
      setSelectedTargetIds((prev) =>
        mergeChannelTargetsOnLoad(serverTargetIds, prev, editedDuringLoad),
      );
    }

    const activeDataCenterName = dataCenters.find(({ channels }) =>
      channels.some(({ name }) => name === currentChannel?.name),
    )?.name;

    if (!activeDataCenterTab && activeDataCenterName) {
      setActiveDataCenterTab(activeDataCenterName);
    }
  }, [
    activeDataCenterTab,
    channelMap,
    channelQueryLoading,
    currentChannel,
    dataCenters,
    setActiveDataCenterTab,
  ]);

  return {
    channelQueryLoading,
    currentChannel,
    currentChannelTargetIds,
    handleSelectTarget,
    selectedTargetIds,
  };
}
